import type { SiteConfig } from "@school-sales-ai/shared";
import { Switch } from "@/components/ui/switch";
import { SiteToggleField } from "./SiteFields";

type Hours = NonNullable<SiteConfig["location"]["hours"]>;

/** Monday first: the order `siteConfigSchema` stores them in. */
const DAYS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"] as const;

/** What a business that just switched hours on most likely has; every row is editable. */
const DEFAULT_HOURS: Hours = [
  ...Array.from({ length: 5 }, () => ({ open: true, from: "09:00", to: "18:00" })),
  { open: true, from: "09:00", to: "14:00" },
  { open: false, from: "09:00", to: "14:00" },
];

/**
 * Opening hours, per weekday. Off stores `null`, which hides the hours on the
 * site rather than inventing a schedule. A closed day keeps its times, so
 * reopening it restores them.
 */
export function SiteHoursEditor({
  hours,
  onChange,
  errorAt,
}: {
  hours: SiteConfig["location"]["hours"];
  onChange: (hours: SiteConfig["location"]["hours"]) => void;
  /** The validation message for row `i`, if any. */
  errorAt: (index: number) => string | undefined;
}): React.JSX.Element {
  function setDay(index: number, patch: Partial<Hours[number]>): void {
    if (!hours) return;
    onChange(hours.map((day, i) => (i === index ? { ...day, ...patch } : day)));
  }

  return (
    <div className="flex flex-col gap-3">
      <SiteToggleField
        label="Mostrar horario"
        hint="Apagado, el sitio no muestra horario."
        checked={hours !== null}
        onChange={(on) => onChange(on ? DEFAULT_HOURS : null)}
      />
      {hours && (
        <ul className="flex flex-col gap-2">
          {hours.map((day, i) => {
            const error = errorAt(i);
            return (
              <li key={DAYS[i]} className="flex flex-col gap-1">
                <div className="flex flex-wrap items-center gap-3 text-[13px]">
                  <span className="w-20 font-medium text-ink-900">{DAYS[i]}</span>
                  <Switch
                    checked={day.open}
                    onCheckedChange={(open) => setDay(i, { open })}
                    aria-label={`${DAYS[i]} abierto`}
                  />
                  {day.open ? (
                    <span className="flex items-center gap-2">
                      <input
                        type="time"
                        value={day.from}
                        onChange={(e) => setDay(i, { from: e.target.value })}
                        aria-label={`${DAYS[i]}, abre`}
                        className="rounded-md border border-ink-300 bg-white px-2 py-1 text-sm"
                      />
                      <span className="text-ink-500">a</span>
                      <input
                        type="time"
                        value={day.to}
                        onChange={(e) => setDay(i, { to: e.target.value })}
                        aria-label={`${DAYS[i]}, cierra`}
                        className="rounded-md border border-ink-300 bg-white px-2 py-1 text-sm"
                      />
                    </span>
                  ) : (
                    <span className="text-ink-500">Cerrado</span>
                  )}
                </div>
                {error && <span className="text-[12px] text-red-500">{error}</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
